import {
  useV2ListAccountHealthSnapshotsQuery,
  V2ListAccountHealthSnapshotsApiArg,
} from "@generated";
import { useMemo } from "react";
import { AccountHealthSnapshot, AccountHealthStatus } from "../types";

interface HookArgs {
  slug: string;
  page?: V2ListAccountHealthSnapshotsApiArg["page"];
}

const useListAccountSnapshots = ({ slug, page }: HookArgs) => {
  const { data, isLoading, isFetching, error, refetch } =
    useV2ListAccountHealthSnapshotsQuery(
      { filter: { account: slug }, include: ["account"], page },
      { skip: !slug }
    );

  // The API returns the same account in `included` for every snapshot in the
  // response (since they all share the same account). Pull the first one for
  // the page heading.
  const account = useMemo(
    () => data?.included?.find((a) => a.attributes?.slug === slug),
    [data, slug]
  );
  const accountName = account?.attributes?.name ?? "";

  const snapshots: AccountHealthSnapshot[] = useMemo(() => {
    if (!data?.data) return [];
    return data.data
      .filter((s) => !!s.id)
      .map((s) => {
        const accountRel = (
          s.relationships as Record<string, { data?: unknown }> | undefined
        )?.account?.data as { id?: number | string } | undefined;
        const attrs = s.attributes ?? {};
        return {
          id: String(s.id),
          weekStart: attrs["week-start"] ?? "",
          status: (attrs.status ?? "healthy") as AccountHealthStatus,
          primaryDriver: attrs["primary-driver"] ?? "",
          signinSlope: Number(attrs["signin-slope"] ?? 0),
          projectSlope: Number(attrs["project-slope"] ?? 0),
          projectBaselineRatio: Number(attrs["project-baseline-ratio"] ?? 0),
          accountId: accountRel?.id ? String(accountRel.id) : "",
          accountName,
          accountSlug: slug,
        };
      });
  }, [data, accountName, slug]);

  const pageMeta = useMemo(
    () => ({
      recordCount: data?.meta?.["record-count"] ?? 0,
      pageCount: data?.meta?.["page-count"] ?? 0,
    }),
    [data]
  );

  return {
    snapshots,
    accountName,
    pageMeta,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};

export default useListAccountSnapshots;
